import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import TelegramConnectModal from "@/components/TelegramConnectModal";
import { CalendarDays, Cloud, Mail, NotebookPen, Music, HeartHandshake, Cpu, MessageSquare, CheckSquare, Activity, Handshake, Sparkles, FileText, Users, Heart, Signal, HeartPulse, BatteryCharging, Chrome, BookmarkPlus, Map, Navigation, Folder, Send, MessageCircle, Bell, ListTodo, ListChecks, Calendar as CalendarIcon, File as FileIcon, HardDrive, CloudUpload, UserCircle, Contact, Watch, Zap as ZapIcon, Compass, Car, Radio, Youtube, Apple } from "lucide-react";
import * as L from "lucide-react";

// Provider icons — falls back to Cpu for unmapped ids.
const PROVIDER_ICONS = {
  // Essentials
  calendar: CalendarDays,
  weather: Cloud,
  gmail: Mail,
  // Tasks
  todoist: ListTodo,
  google_tasks: ListChecks,
  apple_reminders: Bell,
  microsoft_todo: CheckSquare,
  // Notes
  notion: NotebookPen,
  google_docs: FileText,
  apple_notes: NotebookPen,
  onenote: NotebookPen,
  // Files
  google_drive: HardDrive,
  dropbox: CloudUpload,
  onedrive: Folder,
  // Contacts
  google_contacts: Users,
  icloud_contacts: UserCircle,
  phone_contacts: Contact,
  // Messaging
  telegram: Send,
  sms: MessageSquare,
  whatsapp: MessageCircle,
  slack: MessageSquare,
  microsoft_teams: Users,
  // Health
  health: HeartPulse,
  google_fit: Activity,
  oura: Watch,
  whoop: ZapIcon,
  fitbit: Activity,
  // Browser
  chrome_ext: Chrome,
  safari_ext: Compass,
  // Location
  google_maps: Map,
  apple_maps: Navigation,
  uber: Car,
  // Music
  spotify: Music,
  apple_music: Music,
  youtube_music: Youtube,
};

// Category header icons — a lucide component per category id.
const CATEGORY_ICONS = {
  essentials: Sparkles,
  tasks: CheckSquare,
  notes: NotebookPen,
  files: Folder,
  contacts: Users,
  messaging: MessageSquare,
  health: HeartPulse,
  browser: Chrome,
  location: Map,
  music: Music,
};

// Hero-feature icons — the "game-changing feature" strip on each category.
const HERO_ICONS = {
  handshake: Handshake,
  sparkles: Sparkles,
  "file-text": FileText,
  heart: Heart,
  signal: Signal,
  "battery-charging": BatteryCharging,
  "bookmark-plus": BookmarkPlus,
  navigation: Navigation,
};

export function IntegrationsScreen({ user, refresh, go }) {
  const [tiles, setTiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [weatherModal, setWeatherModal] = useState(false);
  const [telegramModal, setTelegramModal] = useState(false);
  // Track which category sections are expanded. Essentials is always open,
  // and any category with at least one connected tile auto-opens on load.
  const [openCats, setOpenCats] = useState({});

  const load = async () => {
    setLoadingList(true);
    try {
      const [ls, act] = await Promise.all([
        api.get("/integrations/list"),
        api.get("/integrations/activity"),
      ]);
      const gotTiles = ls.data.tiles || [];
      const gotCats = ls.data.categories || [];
      setTiles(gotTiles);
      setCategories(gotCats);
      // Auto-open any category that has at least one connected tile so users
      // never miss a "you already turned this on" state.
      const nextOpen = {};
      for (const c of gotCats) {
        const catTiles = gotTiles.filter(t => t.category === c.id);
        nextOpen[c.id] = c.id === "essentials" || catTiles.some(t => t.connected);
      }
      setOpenCats(prev => ({ ...nextOpen, ...prev })); // preserve user's manual toggles across reloads
      setActivity(act.data || []);
    } catch (e) {
      toast.error("Couldn't load connections");
    } finally { setLoadingList(false); }
  };
  useEffect(() => { load(); }, []);

  const disconnectWeather = async () => {
    try {
      await api.post("/integrations/weather/disconnect");
      await refresh(); load();
      toast.success("Weather disconnected");
    } catch { toast.error("Couldn't disconnect"); }
  };

  const disconnectTelegram = async () => {
    try {
      await api.delete("/channels/telegram");
      load();
      toast.success("Telegram disconnected");
    } catch { toast.error("Couldn't disconnect Telegram"); }
  };

  const syncContacts = async () => {
    try {
      const { data } = await api.post("/integrations/contacts/sync");
      toast.success(`${data.contacts} contacts, ${data.person_edges + data.company_edges} relationships`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't sync contacts");
    }
  };

  const notifyMe = async (id, name) => {
    try {
      await api.post(`/integrations/${id}/notify-me`);
      toast.success(`We'll email you when ${name} is ready`);
      load();
    } catch { toast.error("Couldn't join waitlist"); }
  };

  const liveTiles = tiles.filter(t => t.status !== "coming_soon");
  const connectedCount = liveTiles.filter(t => t.connected).length;
  const readyCount = liveTiles.length;
  // Total across everything (live + coming) so the hero counter tells the full story.
  const totalCount = tiles.length;

  const handleTileConnect = (t) => {
    if (t.status === "coming_soon") return notifyMe(t.id, t.name);
    if (t.id === "calendar") return go("calendar");
    if (t.id === "weather") return setWeatherModal(true);
    if (t.id === "telegram") return setTelegramModal(true);
    if (t.id === "gmail") {
      if (t.meta?.needs_reconnect) {
        toast("Reconnect Google Calendar to add Gmail access.", { icon: "🔁" });
        return go("calendar");
      }
      if (t.connected) return go("emails");
      toast("Connect Google Calendar first — it gives Rammy Gmail access too.", { icon: "🔗" });
      return go("calendar");
    }
    if (t.id === "google_contacts") {
      if (t.connected) return syncContacts();
      if (t.meta?.needs_reconnect) {
        toast("Reconnect Google to let Rammy read your contacts.", { icon: "🔁" });
        return go("calendar");
      }
      toast("Connect Google Calendar first — it gives Rammy your contacts too.", { icon: "🔗" });
      return go("calendar");
    }
  };

  const handleTileDisconnect = (t) => {
    if (t.id === "weather") return disconnectWeather();
    if (t.id === "calendar") return go("calendar");
    if (t.id === "gmail") return go("calendar");
    if (t.id === "google_contacts") return go("calendar");
    if (t.id === "telegram") return disconnectTelegram();
  };

  const renderTile = (t) => (
    <ProviderTile
      key={t.id}
      tile={t}
      onConnect={() => handleTileConnect(t)}
      onDisconnect={() => handleTileDisconnect(t)}
    />
  );

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="integrations-screen">
      {/* HERO */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">Connections</div>
          <h1 className="text-2xl md:text-3xl font-display mt-1">Give Rammy helpful context.</h1>
          <p className="text-sm text-[var(--rammy-text-sec)] mt-1 leading-relaxed max-w-xl">
            Every provider unlocks a new way Rammy can help. Read-only by default, always in your control — every action shows up in the trail below.
          </p>
        </div>
        <div className="rounded-2xl bg-[var(--rm-ink-900)] text-white px-4 py-3 flex items-center gap-3 shrink-0" data-testid="integrations-count-card">
          <L.PlugZap size={16} className="text-[var(--rammy-cyan)]"/>
          <div className="leading-tight">
            <div className="font-mono text-lg">
              <span data-testid="integrations-count">{connectedCount}</span>
              <span className="text-white/40">/{readyCount} live</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold" data-testid="integrations-total">
              {totalCount} providers, all working
            </div>
          </div>
        </div>
      </header>

      {loadingList && <div className="py-16 text-sm text-[var(--rammy-text-muted)] text-center">Loading providers…</div>}

      {/* CATEGORY SECTIONS */}
      {!loadingList && categories.map((cat) => {
        const catTiles = tiles.filter(t => t.category === cat.id);
        if (catTiles.length === 0) return null;
        const isOpen = !!openCats[cat.id];
        const CatIcon = CATEGORY_ICONS[cat.id] || Cpu;
        const HeroIcon = cat.hero ? (HERO_ICONS[cat.hero.icon] || Sparkles) : null;
        const catConnected = catTiles.filter(t => t.connected).length;
        return (
          <section key={cat.id} data-testid={`cat-${cat.id}`}>
            <button
              type="button"
              onClick={() => setOpenCats(v => ({ ...v, [cat.id]: !isOpen }))}
              className="w-full flex items-center justify-between px-1 py-2 group"
              data-testid={`cat-${cat.id}-toggle`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition ${catConnected > 0 ? "bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]" : "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)]"}`}>
                  <CatIcon size={16}/>
                </span>
                <div className="text-left min-w-0">
                  <div className="font-display text-base md:text-lg leading-tight">{cat.name}</div>
                  <div className="text-[11px] text-[var(--rammy-text-muted)] truncate">{cat.tagline}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {catConnected > 0 && (
                  <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)] text-[var(--rammy-text)] px-2 py-0.5 rounded-full font-semibold">
                    {catConnected} on
                  </span>
                )}
                <span className="text-[10px] text-[var(--rammy-text-muted)]">{catTiles.length}</span>
                <L.ChevronDown size={14} className={`text-[var(--rammy-text-muted)] transition-transform ${isOpen ? "rotate-180" : ""}`}/>
              </div>
            </button>

            {isOpen && (
              <div className="mt-3 space-y-3 fade-up">
                {cat.hero && HeroIcon && (
                  <div
                    className="rounded-2xl bg-gradient-to-br from-[#FFF3E8] via-[#FFEEF6] to-white border border-[var(--rammy-border)] p-4 flex items-start gap-3"
                    data-testid={`cat-${cat.id}-hero`}
                  >
                    <span className="w-10 h-10 rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] flex items-center justify-center shrink-0 text-[var(--rammy-cyan-dark)]">
                      <HeroIcon size={18}/>
                    </span>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">What this unlocks</div>
                      <div className="font-display text-sm md:text-base mt-0.5">{cat.hero.name}</div>
                      <p className="text-xs text-[var(--rammy-text-sec)] mt-1 leading-relaxed">{cat.hero.line}</p>
                    </div>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {catTiles.map(renderTile)}
                </div>
              </div>
            )}
          </section>
        );
      })}

      {/* ACTIVITY LOG — the transparency trail */}
      <section>
        <div className="flex items-center justify-between px-1 mb-3">
          <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold flex items-center gap-1.5">
            <L.History size={12}/> What Rammy did
          </div>
          {activity.length > 0 && (
            <button
              onClick={load}
              className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold hover:underline"
              data-testid="activity-refresh"
            >
              Refresh
            </button>
          )}
        </div>
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] divide-y divide-[var(--rammy-border)] overflow-hidden" data-testid="activity-list">
          {activity.length === 0 && (
            <div className="p-8 flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[var(--rammy-cyan)]/10 flex items-center justify-center">
                <L.Sparkles size={20} className="text-[var(--rammy-cyan-dark)]"/>
              </div>
              <div className="text-sm font-medium">Nothing to trail yet.</div>
              <p className="text-xs text-[var(--rammy-text-muted)] max-w-xs leading-relaxed">
                Connect Calendar, Weather or Gmail above and every action Rammy takes will appear here — with a timestamp and the exact provider it touched.
              </p>
              <button
                onClick={() => go("calendar")}
                className="mt-1 text-[11px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold hover:underline"
                data-testid="activity-empty-start"
              >
                Start with Calendar →
              </button>
            </div>
          )}
          {activity.slice(0, 10).map(e => (
            <div key={e.id} className="px-4 py-3 flex items-start gap-3" data-testid={`activity-row-${e.id}`}>
              <span className="w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center shrink-0">
                <L.Activity size={14} className="text-[var(--rammy-cyan-dark)]"/>
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm">{e.summary}</div>
                <div className="text-[11px] text-[var(--rammy-text-muted)] mt-0.5 font-mono">
                  {e.provider} · {new Date(e.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rituals bridge — pointer to where daily briefings now live */}
      <button
        onClick={() => go("rituals")}
        className="w-full text-left rounded-3xl bg-gradient-to-br from-[#FFF3E8] via-[#FFEEF6] to-white border border-[var(--rammy-border)] p-5 hover:border-[var(--rammy-cyan)] transition flex items-center gap-4 group"
        data-testid="integrations-rituals-link"
      >
        <span className="w-12 h-12 rounded-2xl bg-[var(--rm-surface)] flex items-center justify-center shrink-0 border border-[var(--rammy-border)]">
          <L.Sunrise size={20} className="text-[var(--rammy-cyan-dark)]"/>
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">Daily rhythms moved</div>
          <div className="mt-0.5 font-display text-base">Morning briefing & evening wind-down live in Rituals →</div>
          <div className="text-xs text-[var(--rammy-text-muted)] leading-relaxed mt-0.5">
            Connections give Rammy context. Rituals are the moments it uses that context for you.
          </div>
        </div>
        <L.ArrowUpRight size={16} className="text-[var(--rammy-text-muted)] group-hover:text-[var(--rammy-cyan-dark)] shrink-0"/>
      </button>

      {weatherModal && (
        <WeatherConnectModal
          onClose={() => setWeatherModal(false)}
          onDone={async () => { setWeatherModal(false); await refresh(); load(); }}
        />
      )}

      {telegramModal && (
        <TelegramConnectModal
          onClose={() => setTelegramModal(false)}
          onDone={async () => { setTelegramModal(false); load(); }}
        />
      )}
    </div>
  );
}

export function ProviderTile({ tile, onConnect, onDisconnect }) {
  const Icon = PROVIDER_ICONS[tile.id] || Cpu;
  const soon = tile.status === "coming_soon";
  const on = tile.connected;
  return (
    <div
      className={`group rounded-2xl border p-4 flex flex-col gap-3 transition ${
        on
          ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/8 shadow-[0_4px_16px_rgba(20,184,166,0.08)]"
          : soon
          ? "border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)]/40 hover:border-[var(--rammy-cyan)]/40"
          : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)] hover:shadow-[0_4px_20px_rgba(17,19,24,0.05)]"
      }`}
      data-testid={`tile-${tile.id}`}
      data-connected={on ? "true" : "false"}
    >
      <div className="flex items-start justify-between">
        <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
          on
            ? "bg-[var(--rammy-cyan)]/25 text-[var(--rammy-cyan-dark)]"
            : "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)]"
        }`}>
          <Icon size={18}/>
        </span>
        {on && (
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)] text-[var(--rammy-text)] px-2 py-0.5 rounded-full font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--rm-ink-900)]"/> On
          </span>
        )}
        {soon && !on && (
          <span className="text-[9px] uppercase tracking-widest bg-[var(--rm-surface)] text-[var(--rammy-text-muted)] border border-[var(--rammy-border)] px-2 py-0.5 rounded-full font-semibold">Soon</span>
        )}
      </div>
      <div>
        <div className="font-medium">{tile.name}</div>
        <div className="text-xs text-[var(--rammy-text-sec)] leading-relaxed min-h-[32px] mt-0.5">{tile.tagline}</div>
      </div>
      {(tile.meta?.city || tile.meta?.source || tile.meta?.email) && (
        <div className="text-[11px] font-mono text-[var(--rammy-text-muted)] truncate" data-testid={`tile-${tile.id}-meta`}>
          {tile.meta?.city && <span>{tile.meta.city}</span>}
          {tile.meta?.source && !tile.meta?.city && <span>via {tile.meta.source}</span>}
          {tile.meta?.email && <span> · {tile.meta.email}</span>}
        </div>
      )}
      <div className="mt-auto pt-1">
        {soon ? (
          <button
            onClick={onConnect}
            className="text-xs px-3 py-1.5 rounded-full border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] hover:text-[var(--rammy-cyan-dark)] transition"
            data-testid={`tile-${tile.id}-notify`}
          >
            Notify me
          </button>
        ) : on ? (
          <div className="flex items-center gap-2">
            <button
              onClick={onConnect}
              className="text-xs px-3 py-1.5 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] transition font-medium"
              data-testid={`tile-${tile.id}-manage`}
            >
              Manage
            </button>
            <button
              onClick={onDisconnect}
              className="text-xs px-3 py-1.5 rounded-full text-[var(--rammy-text-muted)] hover:text-red-600 transition"
              data-testid={`tile-${tile.id}-disconnect`}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <button
            onClick={onConnect}
            className="text-xs px-3.5 py-1.5 rounded-full bg-[var(--rm-ink-900)] text-white hover:opacity-90 font-medium transition"
            data-testid={`tile-${tile.id}-connect`}
          >
            Connect →
          </button>
        )}
      </div>
    </div>
  );
}

export function WeatherConnectModal({ onClose, onDone }) {
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!city.trim()) return;
    setSaving(true); setError("");
    try {
      await api.post("/integrations/weather/connect", { city: city.trim() });
      toast.success("Weather connected");
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't find that city.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="weather-modal">
      <div className="w-full max-w-sm rounded-3xl bg-[var(--rm-surface)] p-6 fade-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-[var(--rammy-cyan)]/20 flex items-center justify-center"><L.Cloud size={16} className="text-[var(--rammy-cyan-dark)]"/></span>
          <div>
            <div className="font-display text-lg">Connect Weather</div>
            <div className="text-xs text-[var(--rammy-text-muted)]">Open-Meteo · free · no account</div>
          </div>
        </div>
        <label className="mt-4 block text-xs uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">City</label>
        <Input
          value={city}
          onChange={e => setCity(e.target.value)}
          placeholder="e.g. San Francisco, Bangalore, Berlin"
          className="mt-1"
          data-testid="weather-city-input"
          onKeyDown={e => e.key === "Enter" && save()}
          autoFocus
        />
        {error && <div className="mt-2 text-xs text-red-600" data-testid="weather-error">{error}</div>}
        <div className="mt-2 text-[11px] text-[var(--rammy-text-muted)]">
          Rammy uses this only to weave weather into your briefings. Change or remove it any time.
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost" className="btn-pill border border-[var(--rammy-border)]" data-testid="weather-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !city.trim()} className="btn-pill btn-primary" data-testid="weather-save">
            {saving ? <L.Loader2 size={14} className="animate-spin"/> : "Connect"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TestEmailButton({ addr }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/briefing/send-test-email");
      setSent(true);
      toast.success(`Test sent to ${data.to}`);
      setTimeout(() => setSent(false), 6000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={test}
      disabled={busy || !addr}
      data-testid="delivery-email-test"
      className="mt-2 text-[11px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan)] hover:text-white disabled:opacity-40"
    >
      {busy ? "Sending…" : sent ? "✓ Sent — check your inbox" : "Send a test email"}
    </button>
  );
}

export function DeliveryToggle({ label, hint, checked, onChange, testid, extra, badge, disabled }) {
  return (
    <div className={`rounded-2xl bg-white/5 border border-white/10 p-3 ${disabled ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            {label} {badge}
          </div>
          <div className="text-xs text-white/50 mt-0.5 leading-relaxed">{hint}</div>
          {extra}
        </div>
        <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid}/>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Inbox screen — where scheduled briefings land in-app.
 * ---------------------------------------------------------------------- */

export function WindDownRecipeCard({ onNav }) {
  const [recipe, setRecipe] = useState({ enabled: false, time_local: "21:00" });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/list");
      setRecipe(data.recipes?.wind_down || { enabled: false, time_local: "21:00" });
    } catch { /* non-blocking */ }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/integrations/wind-down/run");
      setResult(data);
    } catch { toast.error("Wind-down didn't run — try again"); }
    finally { setRunning(false); }
  };

  return (
    <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5" data-testid="recipe-wind-down">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Daily recipe</div>
          <div className="mt-1 text-lg font-display">Evening wind-down</div>
          <p className="text-sm text-[var(--rammy-text-sec)] mt-1 max-w-md leading-relaxed">
            Tomorrow at a glance · a suggested alarm · one gentle journal prompt.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold px-2 py-1 rounded-full bg-[var(--rammy-cyan)]/10">
          {recipe.enabled ? `On · ${recipe.time_local}` : "Off"}
        </span>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button onClick={run} disabled={running} className="btn-pill h-9 px-4 bg-[var(--rm-ink-900)] text-white hover:opacity-90" data-testid="recipe-wind-down-run">
          {running ? <><L.Loader2 size={14} className="animate-spin mr-1.5"/> Composing…</> : <><L.Zap size={14} className="mr-1.5"/> Run wind-down now</>}
        </Button>
        <button onClick={onNav} className="text-xs text-[var(--rammy-cyan-dark)] hover:underline font-medium">
          {recipe.enabled ? "Change time" : "Turn on in Settings"} →
        </button>
      </div>
      {result && (
        <div className="mt-4 rounded-2xl bg-[var(--rammy-surface-alt)] p-4 text-sm leading-relaxed whitespace-pre-wrap fade-up" data-testid="wind-down-output">
          {result.body}
          <div className="mt-3 text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]">
            {result.event_count} tomorrow event{result.event_count === 1 ? "" : "s"}
            {result.suggested_alarm && ` · alarm ${result.suggested_alarm}`}
          </div>
        </div>
      )}
    </div>
  );
}
